import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Paper, Card, Box, Grid,CardHeader, CardContent, Alert } from '@mui/material';
import InfoCard from './InfoCard';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PieChartIcon from '@mui/icons-material/PieChart';
import { pink, cyan } from '@mui/material/colors';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { format, sub } from 'date-fns';
import { getDashboardData, getDashboardBannerData } from 'services/dashboard';
import FiltersBar from './FiltersBar';
import GenericChart from 'core/components/charts/chartjs';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DataUsageIcon from '@mui/icons-material/DataUsage';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [ dashboardBanner, setDashboardBanner ] = useState(false);
  const [dashboardFilters, setDashboardFilter] = useState(false);
  const [chartData, setchartData] = useState(false);

  const timeFrameDates = useMemo(() => {
    const todayDate =  new Date().getTime();
    const seventhDay = sub(new Date(), {days: 7}).getTime();
    const thirtyDay = sub(new Date(), {days: 30}).getTime();
    return {
      TODAY: {from: todayDate, to: todayDate},
      LAST_7_DAYS: {from: todayDate, to: seventhDay},
      LAST_30_DAYS: {from: todayDate, to: thirtyDay},
    }
  }, []);

  useEffect(() => {
    getBanner();
  }, []);

  // Get dashboard data
  const getData = useCallback((data) => {
    const {librarySelected, timeFrame} = data;
    setDashboardFilter({...data});
    if(librarySelected?.libraryId){
      const postData={
        propertyId: librarySelected?.libraryId,
        propertyType: 'LIBRARY',
        timeFrame: timeFrame
      };
      
      getDashboardData({...postData}).then(({response, error}) => {
        if(response){
          const { bookedSeats=0, totalSeats=0, revenue=0, bookings=0, vacantSeats=0, incentives=0 } = response;
          const revenuePerSeat = (revenue/bookings) || 0;
          const projectedRevenue = (revenuePerSeat * totalSeats) || 0;
          const revenuePercent = ((revenue*100)/projectedRevenue) || 0;
  
          const resp = {
            ...response,
            bookingPercent: ((bookedSeats * 100) / totalSeats) || 0,
            vacantPercent: ((vacantSeats * 100) / totalSeats) || 0,
            revenuePercent,
            incentivePercent: ((incentives*100)/incentives) || 0
          };
          setDashboardData({...resp});
          buildBarGraphData(resp);
        } else {
          setDashboardData({});
        }
      });
      
    }
  }, []); 

  /* Functions */
  
  const getBanner = () => {
    getDashboardBannerData().then(({response, error}) => {
      if(response){
        setDashboardBanner({...response});
      } else {
        setDashboardBanner(false);
      }
    });
  }

  const buildBarGraphData = (dashboardResponse={}) => {;
    const bookingChart=getBookingGraphData(dashboardResponse?.bookingsByDate);
    const revenueChart=getRevenueGraphData(dashboardResponse?.revenueByDate);
    const data = {
      booking: {...bookingChart},
      revenue: {...revenueChart},
    }
    setchartData({...data});
  };

  function getBookingGraphData(bookingsByDate) {
    let data=false;
    if(bookingsByDate?.length > 0){

      const xAxis = bookingsByDate?.map(val => val.date);
      const seriresData = bookingsByDate?.map(val => val.count);
      data = {
            data: { 
              labels: xAxis,
              datasets: [{
                label: 'Bookings',
                data: [...seriresData],
                backgroundColor: pink[500],
                borderRadius: 4
              }],
            },
            type: 'bar'
          };
    }
    return data;
  }

  function getRevenueGraphData(revenueByDate) {
    let data=false;
    if(revenueByDate?.length > 0){

      const xAxis = revenueByDate?.map(val => val.date);
      const seriresData = revenueByDate?.map(val => val.total);
      data = {
            data: { 
              labels: xAxis,
              datasets: [{
                label: 'Revenue',
                data: [...seriresData],
                backgroundColor: cyan[500],
                borderRadius: 4
              }],
            },
            type: 'bar'
          };
    }
    return data;
  }

  const buildMoreLink = useCallback((_link, isLibraryAsParam=false) => {
    const {librarySelected, timeFrame} = dashboardFilters;

    if(timeFrameDates[timeFrame]?.from && timeFrameDates[timeFrame]?.to && librarySelected?.libraryId){
      const from = format(timeFrameDates[timeFrame]?.from, 'yyyy-MM-dd');
      const to = format(timeFrameDates[timeFrame]?.to, 'yyyy-MM-dd');
      return `${_link}${isLibraryAsParam ? librarySelected?.libraryId : ''}?${!isLibraryAsParam ? `libraryId=${librarySelected?.libraryId}&` : ''}from=${from}&to=${to}`;
    }
    return false;
  }, [dashboardFilters, timeFrameDates]);

  const moreLinks = useMemo(() => {
    return {
      incentives: buildMoreLink('/reports/incentive/'),
      expenses: buildMoreLink('/expenses/'),
      dues: buildMoreLink('/dues/'),
      bookings: (buildMoreLink('/property/seat-availability/', true) || '').split('?')[0],
      vaccant: (buildMoreLink('/property/seat-availability/', true) || '').split('?')[0],
    }
  }, [buildMoreLink]);

  return (
      <Paper elevation={0} sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )", background: 'transparent' }}>
        <Box noValidate  sx={{ mt: 2 }} >
          <Grid container rowSpacing={3} columnSpacing={3} item xs={12} sm={12} md={12} xl={12} lg={12}>
            {dashboardBanner ? (
              <Grid  sx={{mb: 2, width: '100%'}} >
                <Alert onClose={() => setDashboardBanner(false)} severity="error" elevation={0} variant="filled">{dashboardBanner?.bannerText}</Alert>
              </Grid>
            ): null}
            <FiltersBar getDashboardData={getData} />
            <Grid sx={{mt: 1}} container columnSpacing={3} rowSpacing={3}  >
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard title="Bookings" moreLink={moreLinks?.bookings} theme="yellow" subTitle="Today"  value={dashboardData?.bookings} percentage={dashboardData?.bookingPercent} icon={<DateRangeIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard title="Vacant" moreLink={moreLinks?.vaccant} theme="blue" subTitle="Today" percentage={dashboardData?.vacantPercent}  value={<><CurrencyRupeeIcon sx={{verticalAlign: "middle"}} />{dashboardData?.vacantSeats}</>} icon={<PieChartIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard title="Revenue" theme="green" subTitle="This Month" percentage={dashboardData?.revenuePercent}  value={dashboardData?.revenue} icon={<AccountBalanceIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard moreLink={moreLinks?.incentives} title="Incentives" theme="orange" subTitle="This Month" percentage={dashboardData?.incentivePercent}  value={<><CurrencyRupeeIcon sx={{verticalAlign: "middle"}} />{dashboardData?.incentives}</>} icon={<AccountBalanceWalletIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard moreLink={moreLinks?.dues} title="Dues" theme="purple" subTitle="This Month" percentage={100}  value={<><CurrencyRupeeIcon sx={{verticalAlign: "middle"}} />{dashboardData?.dues}</>} icon={<DataUsageIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={6} xl={3} lg={4} >
                <InfoCard moreLink={moreLinks?.expenses} title="Expenses" theme="red" percentage={100}  value={<><CurrencyRupeeIcon sx={{verticalAlign: "middle"}} />{dashboardData?.expenses}</>} icon={<ShoppingBagIcon />} />
              </Grid>
            </Grid>

            <Grid container columnSpacing={3} rowSpacing={3} item xs={12} sm={12} md={12} xl={12} lg={12} >
              {dashboardFilters?.timeFrame !== 'TODAY' ? (
                <>
                  {chartData.booking ? (
                    <Grid item xs={12} sm={12} md={6} xl={6} lg={6}  >
                      <Card elevation={6} sx={{p: 1}}>
                        <CardHeader title="Booking overview" />
                        {/* <ApexChart options={bookingChartData.options} series={bookingChartData.series} type={bookingChartData.chartType} /> */}
                        <CardContent>
                          <GenericChart data={chartData.booking.data} type={chartData.booking.type} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ):null}

                  {chartData.revenue ? (
                    <Grid item xs={12} sm={12} md={6} xl={6} lg={6}  >
                      <Card elevation={6} sx={{p: 1}}>
                        <CardHeader title="Revenue overview" />
                        {/* <ApexChart options={bookingChartData.options} series={bookingChartData.series} type={bookingChartData.chartType} /> */}
                        <CardContent>
                          <GenericChart data={chartData.revenue.data} type={chartData.revenue.type} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ):null}
                </>
              ): null}
            </Grid>

          </Grid>
        </Box>
      </Paper>
  );
}
 
export default React.memo(Dashboard);
